import React, { useEffect } from "react";
import { Form, Input, InputNumber, Select, Spin, Switch } from "antd";
import { useNavigate } from "react-router-dom";

import { useJobList } from "../../../hooks/api/jobs/hooks";
import {
  useRecipientsDetails,
  useRecipientsSave,
} from "../../../hooks/api/recipients/hooks";
import { useMessageApi } from "../../../provider/AntdNotification";
import ButtonWrapper from "../../../views/components/reuseComp/ButtonWrapper";

interface Props {
  qData: any;
  id?: any;
}

const RecipientsForm: React.FC<Props> = ({ qData, id }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const message = useMessageApi();

  const isEditPage = Boolean(id);

  const { mutate: saveRecipients, isPending } = useRecipientsSave({ qData });
  const { data: recipients, isLoading } = useRecipientsDetails(id);
  const { data: jobsData } = useJobList({ isAll: true });

  const jobList = jobsData?.data || [];

  useEffect(() => {
    if (isEditPage && recipients?.data) {
      form.setFieldsValue({
        groupName: recipients.data.groupName,
        emails: recipients.data.emails,
        jobLinked: recipients.data.jobLinked,
        status: recipients.data.status,
        order: recipients.data.order,
        
      });
    }
  }, [isEditPage, recipients, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      saveRecipients(
        { ...values, _id : id },
        {
          onSuccess: () => {
            message.success(
              isEditPage
                ? "Recipients updated successfully"
                : "Recipients created successfully"
            );
            navigate(-1);
          },
           onError: (err: any) => {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            "Failed to save recipients";

          message.error(msg);
        },
        }
      );
    } catch (err) {
      console.error(err);
    }
  };

   const showSpinner = isEditPage && isLoading;

  return (
    <div>
      <div className="bg-[#F9F9F9] p-3 md:p-6 rounded-2xl shadow-sm mb-6">
         <Spin
            spinning={showSpinner}
            tip="Loading recipients details..."
            size="large"
          >
        <Form layout="vertical" form={form}>
          {/* Group Name */}
          <Form.Item
            label="Group Name"
            name="groupName"
            rules={[{ required: true, message: "Please enter group name" }]}
          >
            <Input placeholder="Group Name" />
          </Form.Item>

          {/* Emails */}
          <Form.Item
            label="Emails"
            name="emails"
            rules={[
              {
                validator: async (_, value) => {
                  if (!value || value.length === 0) {
                    throw new Error("Please enter at least one email");
                  }

                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  const invalid = value.some(
                    (email: string) => !emailRegex.test(email)
                  );

                  if (invalid) {
                    throw new Error("One or more emails are invalid");
                  }
                },
              },
            ]}
          >
            <Select
              mode="tags"
              placeholder="Enter email and press Enter"
              tokenSeparators={[","]}
              allowClear
            />
          </Form.Item>

          {/* Job Linked */}
          <Form.Item
            label="Job Linked"
            name="jobLinked"
            rules={[
              {
                required: true,
                message: "Please select at least one Job RefId",
              },
            ]}
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Select Job RefId"
              loading={jobList.length === 0}
              showSearch
              optionFilterProp="children"
            >
              {jobList.map((job: any) => (
                <Select.Option key={job._id} value={job.refId}>
                  {job.jobName}
                  <span className="text-primary font-semibold">
                    {job.refId ? ` (${job.refId})` : ""}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <div className=" grid grid-cols-2 gap-5">
                  <Form.Item
                    label="Status"
                    name="status"
                    valuePropName="checked"
                    rules={[
                      { required: true, message: "Please select status" },
                    ]}
                  >
                    <Switch
                      checkedChildren="Active"
                      unCheckedChildren="Inactive"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Order"
                    name="order"
                    rules={[{ required: true, message: "Please enter order" }]}
                  >
                    <InputNumber
                      placeholder="Order"
                      min={0}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </div>
        </Form>
        </Spin>
      </div>
      

      {/* Buttons */}
      <div className="flex justify-start gap-3 mt-7 sticky bottom-5">
        <ButtonWrapper type="primary" onClick={handleSave} loading={isPending}>
          Save
        </ButtonWrapper>
        <ButtonWrapper onClick={() => navigate(-1)}>
          Cancel
        </ButtonWrapper>
      </div>
    </div>
  );
};

export default RecipientsForm;
